import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { FORMATIONS } from "../sport/formation";
import Header from "../components/Header";
import Avatar from "../components/Avatar";
import TierBadge from "../components/TierBadge";
import { getTier } from "../sport/tiers";
import SquadList from "../sport/components/SquadList";

const PAGE_SIZE = 10;
const MODES = [
  { id: "standard", label: "Standard" },
  { id: "ranked", label: "Ranked" },
  { id: "daily", label: "Daily Challenge" },
];

function formatDateTime(iso) {
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date} · ${time}`;
}

function Record({ entry }) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div className="text-xs tabular-nums">
        <span className="text-emerald-400 font-semibold">{entry.wins}</span>
        <span className="text-zinc-700 mx-0.5">·</span>
        <span className="text-zinc-400">{entry.draws}</span>
        <span className="text-zinc-700 mx-0.5">·</span>
        <span className="text-red-400">{entry.losses}</span>
      </div>
      <span className="text-sm font-bold text-emerald-400 w-9 text-right">
        {entry.rating}
      </span>
    </div>
  );
}

function GoatBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-black tracking-wider rounded-full px-2 py-0.5 border text-amber-300 bg-amber-500/15 border-amber-400/50 shrink-0">
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
        <path d="M5 16 L3 7 L8.5 11 L12 5 L15.5 11 L21 7 L19 16 Z" />
      </svg>
      GOAT
    </span>
  );
}

function Row({ rank, entry, isMe, mode, expanded, roster, onToggle, isTop }) {
  const username = entry.profiles?.username || "player";
  const expandable = !!entry.formation;
  const slots = expandable ? FORMATIONS[entry.formation] : null;
  const tier = mode === "ranked" ? getTier(entry.glicko_rating) : null;

  const Header = expandable ? "button" : "div";
  const headerProps = expandable
    ? { type: "button", onClick: () => onToggle(entry) }
    : {};

  return (
    <div
      className={`rounded-xl border transition
        ${
          isMe
            ? "bg-emerald-500/10 border-emerald-500/50 shadow-lg shadow-emerald-500/10"
            : "bg-zinc-900/40 border-zinc-800"
        }`}
    >
      <Header
        {...headerProps}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left
          ${expandable ? "hover:bg-zinc-900/30 rounded-xl transition" : ""}`}
      >
        <span
          className={`text-xs font-bold tabular-nums w-8 shrink-0
            ${isMe ? "text-emerald-400" : "text-zinc-500"}`}
        >
          {rank}.
        </span>
        <Avatar
          username={username}
          url={entry.profiles?.avatar_url}
          size={32}
        />

        <div className="flex-1 min-w-0 flex items-center gap-2">
          <span
            className={`text-sm font-semibold truncate
              ${isMe ? "text-emerald-200" : "text-zinc-100"}`}
          >
            {username}
          </span>
          {isMe && (
            <span className="text-emerald-500/70 text-xs shrink-0">you</span>
          )}
          {isTop && <GoatBadge />}
          {tier && <TierBadge sr={entry.glicko_rating} />}
        </div>

        {mode === "ranked" ? (
          <div className="flex items-baseline gap-1 shrink-0">
            <span
              className={`text-sm font-bold tabular-nums ${tier?.text || "text-emerald-400"}`}
            >
              {entry.glicko_rating != null
                ? Math.round(entry.glicko_rating)
                : "—"}
            </span>
            <span className="text-[9px] uppercase tracking-widest text-zinc-600">
              SR
            </span>
          </div>
        ) : (
          <Record entry={entry} />
        )}

        {expandable && (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`w-4 h-4 text-zinc-500 transition-transform shrink-0 ${
              expanded ? "rotate-90" : ""
            }`}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        )}
      </Header>

      {expandable && expanded && (
        <div className="border-t border-zinc-800 px-4 py-3">
          {entry.updated_at && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              {formatDateTime(entry.updated_at)}
            </div>
          )}
          {roster === undefined ? (
            <div className="text-xs text-zinc-500 animate-pulse">
              Loading squad…
            </div>
          ) : roster && slots ? (
            <SquadList roster={roster} slots={slots} />
          ) : (
            <div className="text-xs text-zinc-500">Squad unavailable.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode =
    MODES.find((m) => m.id === searchParams.get("mode"))?.id || "standard";
  const setMode = (m) => setSearchParams({ mode: m });

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [myEntry, setMyEntry] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [rosters, setRosters] = useState({}); // user_id -> roster | null (lazy)

  const fetchPage = async (from) => {
    let query = supabase
      .from("leaderboard_entries")
      .select(
        "user_id, wins, draws, losses, rating, points, strength, glicko_rating, glicko_rd, formation, updated_at, profiles(username, avatar_url)",
      )
      .eq("mode", mode);

    if (mode === "ranked") {
      query = query.order("glicko_rating", {
        ascending: false,
        nullsFirst: false,
      });
    } else {
      query = query
        .order("points", { ascending: false, nullsFirst: false })
        .order("wins", { ascending: false })
        .order("losses", { ascending: true });
    }

    const { data, error } = await query.range(from, from + PAGE_SIZE - 1);
    if (error) {
      console.warn("Leaderboard fetch failed:", error);
      setHasMore(false);
      return [];
    }
    setHasMore((data?.length || 0) === PAGE_SIZE);
    return data || [];
  };

  // Initial load + reload on mode change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setEntries([]);
    setExpandedId(null);
    setRosters({});
    (async () => {
      const first = await fetchPage(0);
      if (cancelled) return;
      setEntries(first);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Current user's own entry + precise rank
  useEffect(() => {
    if (!user) {
      setMyEntry(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: entry } = await supabase
        .from("leaderboard_entries")
        .select(
          "user_id, wins, draws, losses, rating, points, strength, glicko_rating, formation, updated_at, profiles(username, avatar_url)",
        )
        .eq("mode", mode)
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled || !entry) {
        setMyEntry(null);
        return;
      }
      const { data: rank } = await supabase.rpc("get_leaderboard_rank", {
        p_mode: mode,
        p_user: user.id,
      });
      if (cancelled) return;
      setMyEntry({ ...entry, rank: rank ?? null });
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, user]);

  const loadMore = async () => {
    setLoadingMore(true);
    const next = await fetchPage(entries.length);
    setEntries((prev) => [...prev, ...next]);
    setLoadingMore(false);
  };

  const toggleExpand = async (entry) => {
    const id = entry.user_id;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (rosters[id] === undefined && entry.formation) {
      const { data } = await supabase
        .from("leaderboard_entries")
        .select("roster")
        .eq("mode", mode)
        .eq("user_id", id)
        .maybeSingle();
      setRosters((prev) => ({ ...prev, [id]: data?.roster ?? null }));
    }
  };

  const myInList = !!user && entries.some((e) => e.user_id === user.id);
  const showPinnedMe = myEntry && !myInList && myEntry.rank != null;

  return (
    <div className="h-screen flex flex-col">
      <Header />

      <main className="flex-1 overflow-y-auto scrollbar-fancy p-6 min-h-0">
        <div className="max-w-2xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-200 transition mb-4"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="w-4 h-4"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back to hub
          </Link>

          <h1 className="text-3xl font-bold text-zinc-100 mb-6">
            Leaderboards
          </h1>

          {/* Mode tabs */}
          <div className="flex items-center gap-1.5 mb-6">
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMode(m.id)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider transition
                  ${
                    mode === m.id
                      ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
                      : "bg-zinc-900/40 border border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                  }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-16 grid place-items-center text-sm text-zinc-500 animate-pulse">
              Loading…
            </div>
          ) : entries.length === 0 ? (
            <div className="bg-zinc-900/30 border border-zinc-800 border-dashed rounded-xl py-16 grid place-items-center">
              <div className="text-center">
                <div className="text-sm text-zinc-400 mb-1">No entries yet</div>
                <div className="text-xs text-zinc-600">
                  Be the first to set a record
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map((entry, i) => (
                <Row
                  key={entry.user_id}
                  rank={i + 1}
                  entry={entry}
                  isMe={user && entry.user_id === user.id}
                  mode={mode}
                  expanded={expandedId === entry.user_id}
                  roster={rosters[entry.user_id]}
                  onToggle={toggleExpand}
                  isTop={i === 0}
                />
              ))}

              {hasMore && (
                <div className="pt-2 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-5 py-2 rounded-full bg-zinc-900 border border-zinc-800 text-sm font-semibold text-zinc-300 hover:border-zinc-700 transition disabled:opacity-50"
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}

              {showPinnedMe && (
                <>
                  <div className="text-center text-zinc-600 text-sm py-1 tracking-[0.5em] select-none">
                    · · ·
                  </div>
                  <Row
                    rank={myEntry.rank}
                    entry={myEntry}
                    isMe
                    mode={mode}
                    expanded={expandedId === myEntry.user_id}
                    roster={rosters[myEntry.user_id]}
                    onToggle={toggleExpand}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
